import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";

// Critical components (loaded immediately)
import MaintenancePage from "./components/MaintenancePage";
import { HomepageSkeleton, AdminSkeleton, GenericPageSkeleton } from "./components/SkeletonLoaders";

// Lazy-loaded components (loaded on demand)
const HomePage = lazy(() => import("./pages/HomeNova"));
const HomeCatalogo = lazy(() => import("./pages/HomeCatalogo"));
const MultiStepRitualForm = lazy(() => import("./components/MultiStepRitualForm"));
const RitualFormNoPay = lazy(() => import("./components/RitualFormNoPay"));
const AdminLogin = lazy(() => import("./components/AdminLogin"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const PaymentPage = lazy(() => import("./components/PaymentPage"));
const ThankYouPage = lazy(() => import("./components/ThankYouPageNew"));
const ClientLogin = lazy(() => import("./components/ClientLogin"));
const ClientVideoPlayerNew = lazy(() => import("./components/ClientVideoPlayerNew"));
const UpsellTest = lazy(() => import("./components/UpsellTest"));
const Quiz = lazy(() => import("./components/Quiz"));
const TermosDeServico = lazy(() => import("./components/TermosDeServico"));
const PushNotificationPrompt = lazy(() => import("./components/PushNotificationPrompt"));
const AppointmentBooking = lazy(() => import("./components/AppointmentBooking"));
const UpsellPage = lazy(() => import("./components/UpsellPage"));
const ClientDashboard = lazy(() => import("./components/ClientDashboard"));
const ClientAccountLogin = lazy(() => import("./components/ClientAccountLogin"));
const ClientVideoWatch = lazy(() => import("./components/ClientVideoWatch"));
const NotificationPrompt = lazy(() => import("./components/NotificationPrompt"));
const TarotCigano = lazy(() => import("./components/TarotCigano"));
const QuizPage = lazy(() => import("./components/QuizPage"));
const LojaPage = lazy(() => import("./components/LojaPage"));
const PerfumeCruzadoPage = lazy(() => import("./components/PerfumeCruzadoPage"));
const CatalogoPage = lazy(() => import("./pages/CatalogoPage"));
const LinksPage = lazy(() => import("./pages/LinksPage"));
const ObrigadoPage = lazy(() => import("./pages/ObrigadoPage"));
const HomePageRedBlack = lazy(() => import("./components/HomePageRedBlack"));
const LandingPageEspiritual = lazy(() => import("./components/LandingPageEspiritual"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken'); // Changed from 'token' to 'adminToken'
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('adminToken');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      console.log('Tentando login com:', username);
      console.log('API URL:', `${API}/auth/login`);
      
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      console.log('Login response:', response.data);
      
      const { access_token } = response.data;
      localStorage.setItem('adminToken', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      await checkAuth();
      return true;
    } catch (error) {
      console.error('Erro no login:', error.response?.data || error.message);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <AdminSkeleton />;
  }
  
  return user ? children : <Navigate to="/admin/login" />;
};

function App() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState(null);

  useEffect(() => {
    checkMaintenanceMode();
  }, []);

  const checkMaintenanceMode = async () => {
    try {
      if (window.location.pathname.startsWith('/admin')) return;

      const response = await axios.get(`${API}/site-config`);
      const siteConfig = response.data;
      
      if (siteConfig.maintenance_mode) {
        const adminToken = localStorage.getItem('adminToken');
        let isAdmin = false;
        
        if (adminToken) {
          try {
            await axios.get(`${API}/auth/me`, {
              headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            isAdmin = true;
          } catch (error) {}
        }
        
        if (!isAdmin) {
          setMaintenanceMode(true);
          setMaintenanceData({
            message: siteConfig.maintenance_message || 'Site em manutencao. Voltaremos em breve!',
            expectedReturn: siteConfig.maintenance_expected_return
          });
        }
      }
    } catch (error) {
      // If error, proceed normally
    }
  };

  if (maintenanceMode) {
    return (
      <MaintenancePage 
        message={maintenanceData.message}
        expectedReturn={maintenanceData.expectedReturn}
      />
    );
  }

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<GenericPageSkeleton />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/catalogo-antigo" element={<HomeCatalogo />} />
            <Route path="/tarot-cigano" element={<TarotCigano />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/quiz-ritual" element={<QuizPage />} />
            <Route path="/termos" element={<TermosDeServico />} />
            <Route path="/agendar-consulta" element={<AppointmentBooking />} />
            <Route path="/ritual/:slug" element={<MultiStepRitualForm />} />
            <Route path="/registro/:slug" element={<RitualFormNoPay />} />
            <Route path="/pagamento" element={<PaymentPage />} />
            <Route path="/upsell" element={<UpsellPage />} />
            <Route path="/obrigado" element={<ObrigadoPage />} />
            <Route path="/teste-upsell" element={<UpsellTest />} />
            <Route path="/teste-multistep/:slug" element={<MultiStepRitualForm />} />
            <Route path="/loja" element={<LojaPage />} />
            <Route path="/perfume-cruzado" element={<PerfumeCruzadoPage />} />
            <Route path="/catalogo" element={<CatalogoPage />} />
            <Route path="/links" element={<LinksPage />} />
            
            {/* Client access routes */}
            <Route path="/client-login" element={<ClientLogin />} />
            <Route path="/client-video" element={<ClientVideoPlayerNew />} />
            
            {/* Minha Conta - Client permanent account routes */}
            <Route path="/minha-conta" element={<ClientDashboard />} />
            <Route path="/minha-conta/login" element={<ClientAccountLogin />} />
            <Route path="/minha-conta/assistir" element={<ClientVideoWatch />} />
            
            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route 
              path="/admin/*" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
          </Routes>
          </Suspense>
          
          {/* OneSignal Push Notification Prompt */}
          <Suspense fallback={null}>
            <NotificationPrompt />
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;